/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN STUDENT REPORTS API
 * Role: Admin
 * Scope: Tenant-based student attendance, session, homework, and tournament reporting
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */
const STUDENT_REPORTS_ENDPOINT = "/admin/students/student_reports.php";

/**
 * Fetch Student Reports
 * 
 * Scenario 1 (All Students Summary):
 *   Pass `{ fromDate, toDate, page, limit }` without a `studentId`.
 *   Returns high-level attendance, homework, and tournament summary metrics across all students.
 * 
 * Scenario 2 (Single Student Detailed Report):
 *   Pass `{ studentId, fromDate, toDate }`.
 *   Returns profile details, attendance summary, session logs, homework assignment logs,
 *   and tournament performance & game review histories.
 * 
 * @param {Object} params - Query parameters
 * @param {number|string} [params.studentId] - Optional specific student ID
 * @param {string} [params.fromDate] - Optional start date (YYYY-MM-DD)
 * @param {string} [params.toDate] - Optional end date (YYYY-MM-DD)
 * @param {number} [params.page=1] - Page number (for summary list)
 * @param {number} [params.limit=20] - Records per page (for summary list)
 * @param {Object} [options={}] - Extra request options (headers, signals, extra params, etc.)
 */
export const getStudentReports = async (
  { studentId = null, fromDate = "", toDate = "", page = 1, limit = 20 } = {},
  options = {}
) => {
  try {
    const { params: extraParams, ...restOptions } = options;

    const queryParams = { ...extraParams };

    if (studentId) {
      queryParams.student_id = studentId;
    } else {
      queryParams.page = Number(page) || 1;
      queryParams.limit = Number(limit) || 20;
    }

    if (fromDate) queryParams.from_date = fromDate;
    if (toDate) queryParams.to_date = toDate;

    const res = await apiCall(
      "GET",
      STUDENT_REPORTS_ENDPOINT,
      null,
      "Admin – Fetch Student Reports",
      {
        params: queryParams,
        ...restOptions,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch student reports",
        reportType: null,
        data: [],
        homework: [],
        tournaments: [],
        summary: null,
        student: null,
        meta: {},
      };
    }

    // Single Student Detailed Report Response
    if (res.report_type === "single_student") {
      return {
        success: true,
        reportType: "single_student",
        student: res.student || null,
        summary: res.summary || null,
        data: res.sessions || [],       // Session-by-session topic & attendance history
        homework: res.homework || [],   // Homework assignments and status history
        tournaments: res.tournaments || [], // Tournament performances and round-by-round game reviews
        meta: {},
      };
    }

    // All Students Aggregate Summary Response
    return {
      success: true,
      reportType: "all_students_summary",
      student: null,
      summary: null,
      data: res.data || [],
      homework: [],
      tournaments: [],
      meta: res.meta || {
        total: 0,
        page: Number(page) || 1,
        total_pages: 0,
        from_date: fromDate || "Beginning",
        to_date: toDate || "Till Date",
      },
    };
  } catch (error) {
    console.error("Admin Fetch Student Reports Error:", error);

    return {
      success: false,
      message: error?.message || "Unexpected error occurred while fetching reports",
      reportType: null,
      data: [],
      homework: [],
      tournaments: [],
      summary: null,
      student: null,
      meta: {},
    };
  }
};